import { FC, FormEvent, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useNotification } from 'lib/context/notification.context'
import { createInventoryInvoice, getInventoryInvoices, InventoryInvoice } from 'services/inventory/invoices'
import { Button } from 'views/components/UI/Buttons'
import { EditInventoryForm, EditFormRow, EditFormLabel, InventoryDetailHeader } from '../super-admin/inventory/styled'

interface InventoryInvoicesDialogProps {
  inventory: { id: number; name: string; companyName?: string }
  onClose?: () => void
  embedded?: boolean
}

dayjs.extend(utc)

const isValidDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

const InventoryInvoicesDialog: FC<InventoryInvoicesDialogProps> = ({ inventory, onClose, embedded = false }) => {
  const { getSuccess } = useNotification()
  const [invoices, setInvoices] = useState<InventoryInvoice[]>([])
  const [invoiceNumber, setInvoiceNumber] = useState<string>('')
  const [invoiceDate, setInvoiceDate] = useState<string>('')
  const [amount, setAmount] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [listError, setListError] = useState<string>('')
  const [formError, setFormError] = useState<string>('')
  const [refresh, setRefresh] = useState<number>(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    getInventoryInvoices(inventory.id)
      .then((data: InventoryInvoice[]) => {
        if (!active) return
        setInvoices(data)
        setListError('')
      })
      .catch(() => {
        if (!active) return
        setInvoices([])
        setListError('No se pudieron cargar las facturas.')
      })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [inventory.id, refresh])

  const saveInvoice = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (isSaving) return

    const normalizedNumber = invoiceNumber.trim()
    const normalizedAmount = amount.trim().replace(',', '.')
    if (!normalizedNumber || normalizedNumber.length > 100) {
      setFormError('Indica un número de factura de hasta 100 caracteres.')
      return
    }
    if (!isValidDate(invoiceDate)) {
      setFormError('Indica una fecha de factura válida.')
      return
    }
    if (!/^(?:0|[1-9]\d{0,12})(?:\.\d{1,2})?$/.test(normalizedAmount) || Number(normalizedAmount) <= 0) {
      setFormError('Indica un importe positivo con hasta dos decimales.')
      return
    }

    setIsSaving(true)
    setFormError('')
    try {
      await createInventoryInvoice(inventory.id, {
        invoice_number: normalizedNumber,
        invoice_date: invoiceDate,
        amount: normalizedAmount,
      })
      setInvoiceNumber('')
      setInvoiceDate('')
      setAmount('')
      setRefresh((value: number) => value + 1)
      getSuccess('Factura registrada')
    } catch (error) {
      setFormError('No se pudo registrar la factura.')
    } finally {
      setIsSaving(false)
    }
  }

  const content = (
    <Box component="form" onSubmit={saveInvoice}>
      <DialogContent>
          {inventory.companyName && <Typography color="text.secondary" sx={{ mb: 2 }}>{inventory.companyName}</Typography>}
          {isLoading ? <CircularProgress size={24} aria-label="Cargando facturas" /> : <>
            {listError && <Typography color="error" sx={{ mb: 1 }}>{listError}</Typography>}
            {!listError && invoices.length === 0 && <Typography sx={{ mb: 2 }}>No hay facturas registradas.</Typography>}
            {invoices.length > 0 && <TableContainer sx={{ maxHeight: 280, mb: 2 }}>
              <Table stickyHeader size="small" aria-label="Facturas del inventario">
                <TableHead><TableRow><TableCell>Número</TableCell><TableCell>Fecha</TableCell><TableCell align="right">Importe</TableCell></TableRow></TableHead>
                <TableBody>{invoices.map((invoice: InventoryInvoice) => <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoice_number}</TableCell>
                  <TableCell>{dayjs.utc(invoice.invoice_date).format('DD/MM/YYYY')}</TableCell>
                  <TableCell align="right">{invoice.amount}</TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </TableContainer>}
          </>}
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Registrar factura</Typography>
          <EditInventoryForm>
            <EditFormRow>
              <EditFormLabel htmlFor="inventory-invoice-number">Número de factura</EditFormLabel>
              <TextField id="inventory-invoice-number" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} inputProps={{ maxLength: 100 }} required fullWidth disabled={isSaving} size="small" variant="outlined" />
            </EditFormRow>
            <EditFormRow>
              <EditFormLabel htmlFor="inventory-invoice-date">Fecha de factura</EditFormLabel>
              <TextField id="inventory-invoice-date" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} required fullWidth disabled={isSaving} size="small" variant="outlined" />
            </EditFormRow>
            <EditFormRow>
              <EditFormLabel htmlFor="inventory-invoice-amount">Importe</EditFormLabel>
              <TextField id="inventory-invoice-amount" value={amount} onChange={(event) => setAmount(event.target.value)} inputProps={{ inputMode: 'decimal' }} required fullWidth disabled={isSaving} size="small" variant="outlined" />
            </EditFormRow>
          </EditInventoryForm>
          {formError && <Typography color="error" sx={{ mt: 1 }}>{formError}</Typography>}
      </DialogContent>
      <DialogActions>
        {onClose && <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>Cerrar</Button>}
        <Button type="submit" className={embedded ? 'inventory-detail-primary-button' : undefined} disabled={isSaving}>Registrar factura</Button>
      </DialogActions>
    </Box>
  )

  if (embedded) return (
    <Box sx={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
      <InventoryDetailHeader><h6>Facturas del inventario</h6></InventoryDetailHeader>
      {content}
    </Box>
  )

  return (
    <Dialog open onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="inventory-invoices-title">
      <DialogTitle id="inventory-invoices-title">Facturas de {inventory.name}</DialogTitle>
      {content}
    </Dialog>
  )
}

export const InventoryInvoicesPanel: FC<{ inventory: { id: number; name: string } }> = ({ inventory }) => (
  <InventoryInvoicesDialog inventory={inventory} embedded />
)

export default InventoryInvoicesDialog
